import {AgencyRepositoryType} from "./interfaces/AgencyRepositoryType.ts";
import axios, {AxiosError, AxiosInstance, AxiosRequestConfig, HttpStatusCode, InternalAxiosRequestConfig} from "axios";
import {AuthenticationRepository} from "./AuthenticationRepository.ts";
import {firstValueFrom} from "rxjs";
import HTTPConverter from "./converters/HTTPConverter.ts";
import generalErrorConverter from "./converters/GeneralErrorConverter.ts";
import {GeneralError} from "51trips-frontend-core";
import {Agency, AgencyFormData, AgencyObservation, CodeFormat, InvitationLandingTemplate, ReferralAgreement, ReviewerType, TripbookPackage} from "../domain/entities/Agency.ts";
import {CreatePaymentPlanPayload, InstallmentDraft, PaymentPlan} from "../domain/entities/PaymentPlan.ts";
import {
    AgencyUser,
    CreateAgencyUserPasswordRequest,
    CreateAgencyUserPhoneRequest,
    GenerateAgencyMagicLinkRequest,
} from "../domain/entities/AgencyUser.ts";

export class AgencyRepository implements AgencyRepositoryType {

    client: AxiosInstance

    get serverBaseURL(): string {
        return import.meta.env.VITE_APP_SERVER_URL
    }

    constructor(authenticationRepository: AuthenticationRepository) {
        this.client = axios.create({
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            timeout: 20000,
            validateStatus: (status: number) => status >= 200 && status < 300
        })

        // get token from async storage and set it to the header
        this.client.interceptors.request.use(
            async (
                config: InternalAxiosRequestConfig
            ): Promise<InternalAxiosRequestConfig & { retry: number }> => {
                const token = await firstValueFrom(authenticationRepository.getIDToken())
                config.headers.set('Authorization', `Bearer ${token}`)

                const configWithRetry = config as InternalAxiosRequestConfig & {
                    retry: number
                }
                return { ...config, retry: configWithRetry.retry === 0 ? 0 : 1 }
            }
        )

        // add reponse data converter
        this.client.interceptors.response.use(
            (response) => HTTPConverter.fromResponse(response),
            async (err: AxiosError) => {
                const config = err.config as AxiosRequestConfig & { retry: number }
                if (err?.response?.status === HttpStatusCode.Unauthorized && config && config.retry > 0) {
                    config.retry = 0
                    return this.client.request(config)
                }
                return generalErrorConverter.fromBackendServerError(err)
            }
        )
    }

    getAgenciesByTourOperatorId(): Promise<Agency[]> {
        return this.client.get(`${this.serverBaseURL}/agencies`)
    }

    getAgencyById(agencyId: string): Promise<Agency> {
        return this.client.get(`${this.serverBaseURL}/agencies/${agencyId}`)
    }

    updateAgency(id: string, data: AgencyFormData): Promise<Agency> {
        const formData = new FormData()
        formData.append('name', data.name)
        formData.append('contactFirstName', data.contactFirstName)
        formData.append('contactLastName', data.contactLastName)

        if (data.logo) formData.append('file', data.logo)
        if (data.primaryColor) formData.append('color', data.primaryColor)
        if (data.coverMainColorHex) formData.append('coverMainColorHex', data.coverMainColorHex)
        if (data.website) formData.append('website', data.website)
        if (data.contactPhone) formData.append('contactPhone', data.contactPhone)
        if (data.contactEmail) formData.append('contactEmail', data.contactEmail)
        if (data.market) formData.append('primaryMarket', data.market)
        if (data.genericCode) formData.append('genericCode', data.genericCode)
        if (data.codeFormat) formData.append('codeFormat', data.codeFormat)
        if (data.codePrefix) formData.append('codePrefix', data.codePrefix)
        if (data.referredByAgencyId) formData.append('referredByAgencyId', data.referredByAgencyId)
        if (data.referredByAgencyId && data.referralCommissionPct != null) formData.append('referralCommissionPct', String(data.referralCommissionPct))
        if (data.referredByAgencyId && data.referralValidFrom != null) formData.append('referralValidFrom', String(data.referralValidFrom))
        if (data.referredByAgencyId && data.referralValidUntil != null) formData.append('referralValidUntil', String(data.referralValidUntil))
        if (data.invitationLandingTemplate) formData.append('invitationLandingTemplate', data.invitationLandingTemplate)
        if (data.numberOfPagesIncluded != null) formData.append('numberOfPagesIncluded', String(data.numberOfPagesIncluded))
        if (data.clientPlannerUrl) formData.append('clientPlannerUrl', data.clientPlannerUrl)
        if (data.invertedLogo != null) formData.append('invertedLogo', String(data.invertedLogo))
        if (data.webLinkQRCredits != null) formData.append('webLinkQRCredits', String(data.webLinkQRCredits))
        if (data.dedicationText) formData.append('dedicationText', data.dedicationText)
        if (data.fiscalName) formData.append('fiscalName', data.fiscalName)
        if (data.fiscalAddress) formData.append('fiscalAddress', data.fiscalAddress)
        if (data.fiscalId) formData.append('fiscalId', data.fiscalId)
        if (data.firstPackage) formData.append('firstPackage', JSON.stringify(data.firstPackage))
        if (data.showLogoInIntro != null) formData.append('showLogoInIntro', String(data.showLogoInIntro))
        if (data.introductionText) formData.append('introductionText', data.introductionText)
        if (data.reviewer) formData.append('reviewer', data.reviewer)
        if (data.invitationEngagementFlow != null) formData.append('invitationEngagementFlow', data.invitationEngagementFlow)
        if (data.tripbookAgencyBindingName) formData.append('tripbookAgencyBindingName', data.tripbookAgencyBindingName)

        return this.client.post(`${this.serverBaseURL}/agencies/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    }

    deleteAgency(agencyId: string): Promise<void> {
        return this.client.delete(`${this.serverBaseURL}/agencies/${agencyId}`)
    }

    getReferralAgreement(agencyId: string): Promise<ReferralAgreement | undefined> {
        return this.client.get(`${this.serverBaseURL}/agencies/${agencyId}/referral-agreement`)
    }

    getReferrals(agencyId: string): Promise<ReferralAgreement[]> {
        return this.client.get(`${this.serverBaseURL}/agencies/${agencyId}/referrals`)
    }

    getAgencyPackages(agencyId: string): Promise<TripbookPackage[]> {
        return this.client.get(`${this.serverBaseURL}/agencies/${agencyId}/packages`)
    }

    addAgencyPackage(agencyId: string, unitPrice: number, quantity: number, date: number, numberOfPagesIncluded?: number): Promise<TripbookPackage> {
        return this.client.post(`${this.serverBaseURL}/agencies/${agencyId}/packages`, {
            unitPrice,
            quantity,
            date,
            numberOfPagesIncluded,
        })
    }

    updateAgencyPackage(agencyId: string, packageId: string, unitPrice: number, quantity: number, date: number, numberOfPagesIncluded?: number): Promise<TripbookPackage> {
        return this.client.put(`${this.serverBaseURL}/agencies/${agencyId}/packages/${packageId}`, {
            unitPrice,
            quantity,
            date,
            numberOfPagesIncluded,
        })
    }

    deleteAgencyPackage(agencyId: string, packageId: string): Promise<void> {
        return this.client.delete(`${this.serverBaseURL}/agencies/${agencyId}/packages/${packageId}`)
    }

    getPaymentPlans(agencyId: string): Promise<PaymentPlan[]> {
        return this.client.get<unknown, PaymentPlan[]>(`${this.serverBaseURL}/agencies/${agencyId}/payment-plans`).then(rejectIfError)
    }

    createPaymentPlan(agencyId: string, payload: CreatePaymentPlanPayload): Promise<PaymentPlan> {
        return this.client.post<unknown, PaymentPlan>(`${this.serverBaseURL}/agencies/${agencyId}/payment-plans`, payload).then(rejectIfError)
    }

    renegotiatePaymentPlan(agencyId: string, planId: string, installments: InstallmentDraft[]): Promise<PaymentPlan> {
        return this.client.put<unknown, PaymentPlan>(`${this.serverBaseURL}/agencies/${agencyId}/payment-plans/${planId}/installments`, {installments}).then(rejectIfError)
    }

    cancelPaymentPlan(agencyId: string, planId: string): Promise<void> {
        return this.client.delete<unknown, void>(`${this.serverBaseURL}/agencies/${agencyId}/payment-plans/${planId}`).then(rejectIfError)
    }

    getAgencyObservations(agencyId: string): Promise<AgencyObservation[]> {
        return this.client.get<unknown, AgencyObservation[]>(`${this.serverBaseURL}/agencies/${agencyId}/observations`).then(rejectIfError)
    }

    addAgencyObservation(agencyId: string, text: string): Promise<AgencyObservation> {
        return this.client.post<unknown, AgencyObservation>(`${this.serverBaseURL}/agencies/${agencyId}/observations`, {text}).then(rejectIfError)
    }

    updateAgencyObservation(agencyId: string, observationId: string, text: string): Promise<AgencyObservation> {
        return this.client.put<unknown, AgencyObservation>(`${this.serverBaseURL}/agencies/${agencyId}/observations/${observationId}`, {text}).then(rejectIfError)
    }

    deleteAgencyObservation(agencyId: string, observationId: string): Promise<void> {
        return this.client.delete<unknown, void>(`${this.serverBaseURL}/agencies/${agencyId}/observations/${observationId}`).then(rejectIfError)
    }

    createAgency(data: AgencyFormData): Promise<Agency> {
        const formData = new FormData()
        formData.append('name', data.name)
        formData.append('contactFirstName', data.contactFirstName)
        formData.append('contactLastName', data.contactLastName)

        if (data.logo) formData.append('file', data.logo)
        if (data.primaryColor) formData.append('color', data.primaryColor)
        if (data.coverMainColorHex) formData.append('coverMainColorHex', data.coverMainColorHex)
        if (data.website) formData.append('website', data.website)
        if (data.contactPhone) formData.append('contactPhone', data.contactPhone)
        if (data.contactEmail) formData.append('contactEmail', data.contactEmail)
        if (data.market) formData.append('primaryMarket', data.market)
        if (data.genericCode) formData.append('genericCode', data.genericCode)
        if (data.codeFormat) formData.append('codeFormat', data.codeFormat)
        if (data.codePrefix) formData.append('codePrefix', data.codePrefix)
        if (data.referredByAgencyId) formData.append('referredByAgencyId', data.referredByAgencyId)
        if (data.referredByAgencyId && data.referralCommissionPct != null) formData.append('referralCommissionPct', String(data.referralCommissionPct))
        if (data.referredByAgencyId && data.referralValidFrom != null) formData.append('referralValidFrom', String(data.referralValidFrom))
        if (data.referredByAgencyId && data.referralValidUntil != null) formData.append('referralValidUntil', String(data.referralValidUntil))
        if (data.invitationLandingTemplate) formData.append('invitationLandingTemplate', data.invitationLandingTemplate)
        if (data.numberOfPagesIncluded !== undefined) formData.append('numberOfPagesIncluded', String(data.numberOfPagesIncluded))
        if (data.clientPlannerUrl) formData.append('clientPlannerUrl', data.clientPlannerUrl)
        if (data.invertedLogo !== undefined) formData.append('invertedLogo', String(data.invertedLogo))
        if (data.webLinkQRCredits !== undefined) formData.append('webLinkQRCredits', String(data.webLinkQRCredits))
        if (data.dedicationText) formData.append('dedicationText', data.dedicationText)
        if (data.fiscalName) formData.append('fiscalName', data.fiscalName)
        if (data.fiscalAddress) formData.append('fiscalAddress', data.fiscalAddress)
        if (data.fiscalId) formData.append('fiscalId', data.fiscalId)
        if (data.firstPackage) formData.append('firstPackage', JSON.stringify(data.firstPackage))
        if (data.showLogoInIntro != null) formData.append('showLogoInIntro', String(data.showLogoInIntro))
        if (data.introductionText) formData.append('introductionText', data.introductionText)
        if (data.reviewer) formData.append('reviewer', data.reviewer)
        if (data.invitationEngagementFlow != null) formData.append('invitationEngagementFlow', data.invitationEngagementFlow)
        if (data.tripbookAgencyBindingName) formData.append('tripbookAgencyBindingName', data.tripbookAgencyBindingName)

        return this.client.put(`${this.serverBaseURL}/agencies`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
    }

    getAgencyUsers(agencyId: string): Promise<AgencyUser[]> {
        // The explicit <any, R> pins axios's contextually-inferred response
        // type (R) up front — needed because chaining .then(rejectIfError)
        // below breaks the "return client.get(url)" contextual inference the
        // rest of this file relies on to unwrap AxiosResponse<T> to T.
        return this.client.get<unknown, AgencyUser[]>(`${this.serverBaseURL}/agencies/${agencyId}/users`).then(rejectIfError)
    }

    createAgencyUserWithPassword(agencyId: string, data: CreateAgencyUserPasswordRequest): Promise<AgencyUser> {
        return this.client.post<unknown, AgencyUser>(`${this.serverBaseURL}/agencies/${agencyId}/users/password`, data).then(rejectIfError)
    }

    createAgencyUserWithPhone(agencyId: string, data: CreateAgencyUserPhoneRequest): Promise<AgencyUser> {
        return this.client.post<unknown, AgencyUser>(`${this.serverBaseURL}/agencies/${agencyId}/users/phone`, data).then(rejectIfError)
    }

    generateAgencyUserMagicLink(agencyId: string, data: GenerateAgencyMagicLinkRequest): Promise<{ link: string }> {
        return this.client.post<unknown, { link: string }>(`${this.serverBaseURL}/agencies/${agencyId}/users/magic-link`, data).then(rejectIfError)
    }

    linkAgencyUserPassword(agencyId: string, uid: string, data: CreateAgencyUserPasswordRequest): Promise<AgencyUser> {
        return this.client.post<unknown, AgencyUser>(`${this.serverBaseURL}/agencies/${agencyId}/users/${uid}/password`, data).then(rejectIfError)
    }

    linkAgencyUserPhone(agencyId: string, uid: string, data: CreateAgencyUserPhoneRequest): Promise<AgencyUser> {
        return this.client.post<unknown, AgencyUser>(`${this.serverBaseURL}/agencies/${agencyId}/users/${uid}/phone`, data).then(rejectIfError)
    }

    linkAgencyUserMagicLink(agencyId: string, uid: string, data: GenerateAgencyMagicLinkRequest): Promise<{ link: string }> {
        return this.client.post<unknown, { link: string }>(`${this.serverBaseURL}/agencies/${agencyId}/users/${uid}/magic-link`, data).then(rejectIfError)
    }

    setAgencyUserDisabled(agencyId: string, uid: string, disabled: boolean): Promise<void> {
        return this.client.put<unknown, void>(`${this.serverBaseURL}/agencies/${agencyId}/users/${uid}`, {disabled}).then(rejectIfError)
    }

    deleteAgencyUser(agencyId: string, uid: string): Promise<void> {
        return this.client.delete<unknown, void>(`${this.serverBaseURL}/agencies/${agencyId}/users/${uid}`).then(rejectIfError)
    }
}

// This repository's response interceptor (see fromBackendServerError above)
// RETURNS a GeneralError on HTTP failure instead of throwing — a pre-existing
// quirk shared by every method in this file, which means the resulting
// promise resolves instead of rejecting and `.catch()` callers never fire.
// The user-management UI needs field-level (400) vs generic (401/404/500)
// error handling per the API spec, so these methods opt back into a real
// rejection instead of silently returning a GeneralError as if it were data.
function rejectIfError<T>(result: T | GeneralError): T {
    if (result instanceof GeneralError) throw result
    return result
}
